import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';

// API基底クラス
class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = '/api/v1') {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // リクエストインターセプター（認証トークン付与）
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // レスポンスインターセプター（エラーハンドリング）
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const { response } = error;

        if (response?.status === 401) {
          // トークン期限切れ - リフレッシュを試行
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const refreshResponse = await this.client.post('/auth/refresh', {
                refresh_token: refreshToken,
              });
              
              const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;
              localStorage.setItem('access_token', access_token);
              localStorage.setItem('refresh_token', newRefreshToken);
              
              // 元のリクエストを再実行
              error.config.headers.Authorization = `Bearer ${access_token}`;
              return this.client.request(error.config);
            } catch (refreshError) {
              // リフレッシュ失敗 - ログアウト
              this.logout();
              toast.error('セッションが期限切れです。再ログインしてください。');
            }
          } else {
            this.logout();
          }
        } else if (response?.status >= 500) {
          toast.error('サーバーエラーが発生しました。しばらく後でお試しください。');
        } else if (response?.data?.error) {
          toast.error(response.data.error);
        }

        return Promise.reject(error);
      }
    );
  }

  private logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }

  // HTTP メソッド
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.delete(url, { data });
    return response.data;
  }

  // 認証状態チェック
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // トークン設定
  setTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  // ログアウト
  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }
}

// グローバルAPIクライアントインスタンス
export const apiClient = new ApiClient();

// 型安全なAPI呼び出し用のヘルパー関数
export const api = {
  get: <T>(url: string, params?: any) => apiClient.get<T>(url, params),
  post: <T>(url: string, data?: any) => apiClient.post<T>(url, data),
  put: <T>(url: string, data?: any) => apiClient.put<T>(url, data),
  delete: <T>(url: string, data?: any) => apiClient.delete<T>(url, data),
};
